import { Modal, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { css, Global } from '@emotion/react';
import { CloseIcon, LockedIcon } from '@sketchnote/icons';
import { FullPageLoader } from '@sketchnote/shared-components';
import { Button } from '@sketchnote/shared-components';
import { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import { useState } from 'react';
import { API } from 'src/api';
import { PageErrorBoundary } from 'src/components/error-boundary/page-error-boundary';
import { NoAccessPage } from 'src/components/error-pages/NoAccessPage';
import { PageNotFound } from 'src/components/error-pages/PageNotFound';
import { SEO } from 'src/components/seo';
import { useSidebarStore } from 'src/components/sidebar/store';
import { ErrorCodes } from 'src/data/error-codes';
import { ChatView } from 'src/features/chat';
import { PdfChat } from 'src/features/pdf-chat/routes/PdfChat';
import { Scrum } from 'src/features/scrum/Scrum';
import { withAuth } from 'src/hoc';
import { useAuth } from 'src/shared/hooks/use-auth';

import { FileHeader } from '../components/FileHeader';
import { FileViewNavbar } from '../components/FileViewNavbar';
import { FileTourData } from '../data/data';
import { useFileAccess } from '../hooks/useFileAccess';
import { FileLayout } from '../layouts/FileLayout';
import { useFileStore } from '../store/useFileStore';
import { getFileType } from '../utils';

const SheetView = dynamic<{ id: string }>(() =>
  import('src/features/sheet/Worksheet').then((mod) => mod)
);
const WhiteBoard = dynamic<{
  id: string;
  memberId: string;
  isPublic?: boolean;
  isReadOnly?: boolean;
}>(() => import('src/features/whiteboard/components').then((mod) => mod.WhiteBoard));
const BoardPage = dynamic<{ boardId: string; type: string }>(() =>
  import('src/features/boards').then((mod) => mod.BoardPage)
);
const Form = dynamic<{ id: string }>(() => import('src/features/form').then((mod) => mod.Form));
const EmbedView = dynamic<{ id: string }>(() =>
  import('src/features/Embed').then((mod) => mod.EmbedView)
);
const DocPage = dynamic<{ docId: string }>(() =>
  import('src/features/docs').then((mod) => mod.DocPage)
);

export const FilePage = withAuth(() => {
  const router = useRouter();
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );

  const { isReadOnly } = useFileAccess();

  const { fetchFile, isSticky } = useFileStore((s) => ({
    fetchFile: s.fetchFile,
    isSticky: s.isSticky,
  }));

  const [page, setPage] = useState(0);
  const item = FileTourData[page];

  const { workspaces } = useSidebarStore();
  const { toggleWorkspace, toggleWorkspaceById } = useSidebarStore();
  const userDetails = useAuth((s) => s.userDetails);
  const fetchUserDetails = useAuth((s) => s.fetchUserDetails);

  const [fileTour, setFileTour] = useState(userDetails?.memberInformation?.fileOnboarded);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [router.query.view]);

  async function fetchCurrentFile() {
    const fileId = router.query.slug as string;
    await fetchFile({ id: fileId });
  }

  //Routes to the primary view when no query parameters are present
  useEffect(() => {
    if (
      router.query.view === undefined ||
      router.query.view === 'undefined' ||
      router.query.viewId === undefined ||
      router.query.viewId === 'undefined'
    ) {
      const type = currentFile?.file_hidden
        ? currentFile?.internalFiles?.[0]?.type
        : currentFile?.primaryFile?.type;

      const view = currentFile?.file_hidden
        ? currentFile?.internalFiles?.[0]?.id
        : currentFile?.primaryFile?.id;

      const viewId = currentFile?.file_hidden
        ? currentFile?.internalFiles?.[0]?.viewId
        : currentFile?.primaryFile?.id;

      if (!type || !view) return;

      router.replace({
        query: {
          ...router.query,
          view: view,
          viewId: viewId,
          type: getFileType({ type, view }),
        },
      });
    }
  }, [currentFile?.primaryFile?.id, currentFile, router.query.view, router.query.viewId]);

  useEffect(() => {
    if (router?.query?.slug) fetchCurrentFile();
  }, [router?.query?.slug]);

  const isActiveViewLocked = useMemo(() => {
    const viewId = router.query.viewId as string;
    const selectedView = currentFile?.internalFiles?.find((f) => f.viewId === viewId);
    if (!viewId) return false;
    if (!selectedView) {
      if (viewId === currentFile?.primaryFile?.id && !currentFile?.file_hidden) return false;
      return true;
    }
    return false;
  }, [currentFile?.internalFiles, currentFile?.primaryFile, router.query.viewId]);

  useEffect(() => {
    if (!currentFile?.primaryFile?.id || !router.query.viewId) return;
    const selectedView =
      currentFile?.internalFiles?.find((f) => f.viewId === router.query.viewId) ||
      currentFile.primaryFile;
    if (router.query.type !== getFileType({ type: selectedView.type, view: selectedView.view })) {
      router.replace({
        query: {
          ...router.query,
          type: getFileType({ type: selectedView.type, view: selectedView.view }),
        },
      });
    }
  }, [currentFile?.primaryFile?.id, router.query.view, router.query.viewId, router.query.type]);

  useEffect(() => {
    if (currentFile?.workspace && Object.keys(currentFile?.workspace).length > 0) {
      if (currentFile?.workspace?.id) {
        toggleWorkspace(true);
        toggleWorkspaceById(true, currentFile?.workspace?.id);
      }
    }
  }, [currentFile?.workspace?.id, workspaces, currentFile?.shared]);

  //Quick fix to ensure ispublicfile header not added.
  useEffect(() => {
    useAuth.setState({ isPublicFile: false });
  }, []);

  useEffect(() => {
    setFileTour(userDetails?.memberInformation?.fileOnboarded);
  }, [userDetails?.memberInformation?.fileOnboarded]);

  const activeInternalFile = useCallback(() => {
    const type = router?.query?.type as string;
    const id = router?.query?.view as string;
    switch (type.toLowerCase()) {
      case 'document':
        return <DocPage docId={id} />;
      case 'whiteboard':
        return (
          <WhiteBoard
            id={id}
            memberId={userDetails?.memberInformation.memberId || ''}
            isReadOnly={isReadOnly}
          />
        );
      case 'form':
        return <Form id={id} />;
      case 'sheet':
        return <SheetView id={id} />;
      case 'embed':
        return <EmbedView id={id} />;
      case 'agile':
        return <Scrum />;
      case 'chat':
        return <ChatView id={id} />;
      case 'pdf_chat':
        return <PdfChat id={id} fileId={router.query.slug as string} />;
      default:
        return <BoardPage boardId={id} type={type} />;
    }
  }, [router.query]);

  const handleCloseFileTourModal = async () => {
    setFileTour(false);
    setPage(0);
    //set the fileOnboarding to false
    await API.post('/settings/onboard', { type: 'file' });
    fetchUserDetails();
  };

  const handleNext = () => {
    if (page === FileTourData.length - 1) {
      handleCloseFileTourModal();
    } else setPage(page + 1);
  };

  const handlePrev = () => {
    if (page === 0) return;
    setPage(page - 1);
  };

  return (
    <FileLayout fileId={router.query.slug as string}>
      <SEO
        title={currentFile?.isLoading ? fileMetaData.title : currentFile?.name || 'Untitled'}
        description={fileMetaData.description}
      />
      <PageErrorBoundary wrapperClass="mt-32">
        <div className="h-full">
          {currentFile?.authorisation && router.query.view ? (
            <div
              className="overflow-auto w-full"
              style={{ height: 'calc((100vh - 50px)' }}
              id="file-wrapper"
            >
              <FileHeader
                key={`header-${router?.query.slug}`}
                fileId={router?.query.slug as string}
                type={router?.query?.type as string}
              />

              {router?.query?.type !== 'form' && router?.query?.type !== 'pdf_chat' ? (
                <FileViewNavbar isSticky={isSticky} />
              ) : (
                <></>
              )}

              <div id="file-child">
                {isActiveViewLocked ? (
                  <div className="w-full h-full relative">
                    <div className="z-0">{activeInternalFile()}</div>
                    <div
                      className="w-full h-full absolute z-10 top-0 bottom-0 right-0 left-0"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(3px)',
                      }}
                    />
                    <div className="absolute top-256 z-20 w-full flex items-center flex-col justify-center text-gray-500">
                      <LockedIcon size={74} />
                      <span className="text-center mt-12">Oops! This view is unavailable</span>
                    </div>
                  </div>
                ) : (
                  activeInternalFile()
                )}
              </div>

              <Modal isOpen={fileTour || false} onClose={handleCloseFileTourModal}>
                <ModalOverlay />

                <ModalContent
                  style={{
                    maxWidth: '450px',
                    maxHeight: '630px',
                    marginTop: '10rem',
                    borderRadius: '8px',
                  }}
                >
                  <div className="flex justify-end h-32 items-center px-15 pt-8">
                    <button aria-label="Close icon" onClick={handleCloseFileTourModal}>
                      <CloseIcon className="text-gray-500 text-xs" />
                    </button>
                  </div>

                  <div className="flex flex-col justify-center px-30 pt-15">
                    <div>
                      <div className="bg-gray-100 rounded-md shadow-md">
                        <img
                          className="w-full h-250 object-fit object-center m-auto p-10"
                          src={item.image}
                        />
                      </div>
                      {item.title && (
                        <p className="mt-16 text-blue-800 font-semibold text-l">{item.title}</p>
                      )}
                      <ul className="list-disc list-outside px-24">
                        {item.content.map((option, i) => {
                          return (
                            <li key={i} className="mt-12 text-gray-600 text-sm">
                              {option.option}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                  <div className="flex px-40 my-25 justify-between">
                    <div className="text-xs text-gray-400 font-regular">
                      {page + 1}/{FileTourData.length} Steps
                    </div>
                    <div>
                      {page !== 0 && (
                        <Button
                          onClick={handlePrev}
                          disabled={page === 0}
                          status="secondary"
                          className="mr-6"
                        >
                          Previous
                        </Button>
                      )}
                      <Button onClick={handleNext} status="primary" className="">
                        {page === FileTourData.length - 1 ? 'Done' : 'Next'}
                      </Button>
                    </div>
                  </div>
                </ModalContent>
              </Modal>

              <Global
                styles={css`
                  body {
                    overflow: hidden;
                  }
                  html,
                  body {
                    overscroll-behavior: none;
                  }
                `}
              />
            </div>
          ) : (!currentFile?.isLoading &&
              currentFile?.errorCode === ErrorCodes['FILE_ACCESS_DENIED']) ||
            currentFile?.errorCode === ErrorCodes['FILE_PERMISSION_DENIED'] ? (
            <NoAccessPage showLogo={false} type={'file'} />
          ) : !currentFile?.isLoading && currentFile?.errorCode === ErrorCodes['FILE_NOT_FOUND'] ? (
            <PageNotFound />
          ) : (
            <FullPageLoader loading />
          )}
        </div>
      </PageErrorBoundary>
    </FileLayout>
  );
});

export const getStaticProps: GetStaticProps = async () => {
  return { props: {} };
};

const fileMetaData = {
  title: 'Sketchnote | Startup lessons and productivity tools',
  description:
    'The all-in-one space for startups and entrepreneurs to learn, create, and collaborate with teammates as you scale your organization',
};
